import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { BRAND, IMAGES } from "@/data";

const LINES = ["Moving Lives.", "Delivering", "With Purpose."];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero({ onQuote }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.5, 0.85]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.heroTruck} alt="Cross country freight at night" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-ink" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/30" />
      <div className="absolute inset-0 radial-purple" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 min-h-screen flex flex-col justify-center pt-28">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-gold/60" />
          <span className="text-xs tracking-[0.45em] uppercase text-gold/90">
            Black-Owned · Lancaster, SC · DOT {BRAND.dot}
          </span>
        </motion.div>

        <h1 className="font-serif font-light leading-[0.92] text-cream text-5xl sm:text-7xl lg:text-[8.5rem]">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="show"
                className={`block ${i === 1 ? "italic text-gold text-glow" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 1 }}
          className="mt-10 max-w-xl text-base sm:text-lg text-cream/70 font-light leading-relaxed"
        >
          Premium local & cross-country freight, elderly & medical transport, and specialized
          Sprinter delivery — built to serve NGOs, care providers, and the communities that count on us.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        >
          <button
            onClick={onQuote}
            data-testid="hero-quote-btn"
            className="btn-gold group flex items-center gap-3 border border-gold text-gold px-8 py-4 tracking-widest text-sm"
          >
            REQUEST A QUOTE
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <div className="flex items-center gap-2 text-cream/60 text-sm">
            <ShieldCheck size={18} className="text-gold" />
            Licensed & Insured · MC {BRAND.mc}
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-cream/40 text-[10px] tracking-[0.4em] uppercase"
      >
        Scroll to explore
      </motion.div>
    </section>
  );
}
